import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Calendar, MapPin, ChevronDown, Ticket } from "lucide-react";
import { StarField } from "../StarField";
import { scrollToId } from "../../lib/scroll";
import { EVENT } from "../../data/content";
import { useLang } from "../../i18n/LanguageContext";

const line = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.35 + i * 0.16, ease: [0.16, 1, 0.3, 1] },
  }),
};

const MaskLine = ({ children, i, className }) => (
  <span className="block overflow-hidden">
    <motion.span custom={i} variants={line} initial="hidden" animate="visible" className={`block ${className}`}>
      {children}
    </motion.span>
  </span>
);

export const Hero = ({ count }) => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBack = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const yPalace = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-[100svh] items-center justify-center overflow-hidden bg-ink-900 grain"
    >
      <motion.div style={{ y: yBack }} className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1774266832118-3f102bcb1c93?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
          alt=""
          className="h-full w-full object-cover opacity-55"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink-900/70 via-ink-900/60 to-ink-900" />
      </motion.div>

      <StarField density={0.00016} />

      <motion.div style={{ y: yPalace }} className="pointer-events-none absolute inset-x-0 bottom-0 h-[45%]">
        <img
          src="https://images.unsplash.com/photo-1661924326425-c14a6426d989?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
          alt=""
          className="h-full w-full object-cover object-top opacity-25 [mask-image:linear-gradient(to_top,black,transparent)]"
        />
      </motion.div>

      <div className="pointer-events-none absolute left-1/2 top-1/3 h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-gold-primary/15 blur-[120px]" />

      <motion.div style={{ opacity }} className="relative z-10 mx-auto max-w-5xl px-6 text-center">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15, duration: 0.8 }}
          data-testid="hero-eyebrow"
          className="mb-6 text-xs uppercase tracking-[0.3em] text-gold-light/80 md:text-sm"
        >
          {t.hero.presents}
        </motion.p>

        <h1 className="font-display font-light leading-[0.92] text-white">
          <MaskLine i={0} className="text-5xl sm:text-7xl lg:text-8xl tracking-tight">SAPIENS</MaskLine>
          <MaskLine i={1} className="text-6xl sm:text-8xl lg:text-9xl font-semibold text-gold-gradient tracking-tight">CELESTIA</MaskLine>
          <MaskLine i={2} className="mt-3 font-display text-3xl italic sm:text-5xl lg:text-6xl text-white/90">{t.hero.runway}</MaskLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="mx-auto mt-7 max-w-xl text-base font-light text-white/70 md:text-lg"
        >
          {t.hero.subtitle}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.8 }}
          className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-4"
        >
          <div className="glass flex items-center gap-2.5 rounded-full px-5 py-3 text-sm text-white/90">
            <Calendar className="h-4 w-4 text-gold-light" /> August 8 – 9, 2026
          </div>
          <a
            href={EVENT.mapsLink}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-venue-map"
            className="glass flex items-center gap-2.5 rounded-full px-5 py-3 text-sm text-white/90 transition-colors hover:text-gold-light"
          >
            <MapPin className="h-4 w-4 text-gold-light" /> {EVENT.venue}
          </a>
          <div className="flex items-center gap-2.5 rounded-full border border-gold-light/40 bg-gold-primary/15 px-5 py-3 text-sm font-medium text-gold-light">
            <Ticket className="h-4 w-4" /> {t.hero.feeChip}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 1.45, duration: 0.6 }}
          data-testid="hero-urgency"
          className="mt-5 inline-flex items-center gap-2 rounded-full bg-carpet/30 px-4 py-2 text-xs font-medium uppercase tracking-[0.15em] text-red-200"
        >
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-red-400" />
          </span>
          {t.hero.urgency}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.5, duration: 0.8 }}
          className="mt-9 flex flex-wrap items-center justify-center gap-4"
        >
          <button
            onClick={() => scrollToId("#register")}
            data-testid="hero-register-btn"
            className="rounded-full bg-gradient-to-r from-gold-light to-gold-primary px-8 py-4 text-sm font-medium tracking-wide text-ink-900 transition-shadow duration-300 hover:shadow-[0_0_30px_rgba(217,119,6,0.6)]"
          >
            {t.hero.cta1}
          </button>
          <button
            onClick={() => scrollToId("#games")}
            data-testid="hero-play-btn"
            className="rounded-full border border-white/25 px-8 py-4 text-sm font-medium tracking-wide text-white transition-colors hover:bg-white/10"
          >
            {t.hero.cta2}
          </button>
        </motion.div>

        {count ? (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.7, duration: 0.8 }}
            className="mt-7 text-sm text-white/50"
          >
            <span className="font-display text-lg text-gold-light">{count}+</span> {t.hero.dreamers}
          </motion.p>
        ) : null}
      </motion.div>

      <motion.button
        onClick={() => scrollToId("#inspiration")}
        style={{ opacity }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        className="absolute bottom-7 left-1/2 z-10 -translate-x-1/2 text-white/50 hover:text-gold-light"
        aria-label="Scroll down"
      >
        <ChevronDown className="h-7 w-7 animate-bounce" />
      </motion.button>
    </section>
  );
};
